/**
 * TelcoTrack — **canonical entry point** for object workflow graph logic.
 *
 * Data: `generated-workflows.ts` (Excel). Built indexes: `PHASE_BY_CODE`, per-type workflows.
 * Each object type has one linear branch in the index, except BUDOVA (three branches; branch = code prefix).
 *
 * **Use this module for:** phase lookup, next/previous step, colors, progress, `isSamePhase`.
 * **Labels & palette keys:** re-exported from `phase-codes.ts` (`resolvePhaseLabel`, `PHASE_COLORS`).
 * **BUDOVA metadata / API variant:** `variants.ts` (`parseWorkflowVariant`).
 * **History-aware previous step:** `helpers.ts` (`filterPreviousPhasesByObjectHistoryTimeline`).
 *
 * See `README.md` in this folder for the full orientation map.
 */

import { GENERATED_WORKFLOWS } from './generated-workflows';
import {
  PHASE_COLORS,
  getPhaseName,
  resolvePhaseLabel,
  normalizeObjectTypeForLookup,
} from './phase-codes';

/** Czech labels and color tokens — prefer `resolvePhaseLabel` in UI. */
export {
  PHASE_COLORS,
  getPhaseName,
  resolvePhaseLabel,
} from './phase-codes';
import { budovaBranchIdFromPhaseCode } from './variants';
import type {
  GeneratedDrawingId,
  GeneratedPhase,
  GeneratedWorkflow,
} from './generated-types';
import type { ObjectTypeWorkflow, WorkflowPhase, WorkflowPhaseDef } from './types';

export type { GeneratedDrawingId, GeneratedPhase, GeneratedWorkflow };

// =============================================================================
// INDEXY
// =============================================================================

const WORKFLOWS_BY_TYPE = new Map<string, GeneratedWorkflow[]>();
const PHASE_BY_CODE = new Map<string, { workflow: GeneratedWorkflow; phase: GeneratedPhase }>();
const DEFAULT_WORKFLOW_BY_TYPE = new Map<string, ObjectTypeWorkflow>();

for (const wf of GENERATED_WORKFLOWS) {
  const list = WORKFLOWS_BY_TYPE.get(wf.objectType) ?? [];
  list.push(wf);
  WORKFLOWS_BY_TYPE.set(wf.objectType, list);
  for (const phase of wf.phases) {
    PHASE_BY_CODE.set(phase.code, { workflow: wf, phase });
  }
}

function buildLinearPhases(wf: GeneratedWorkflow): WorkflowPhaseDef[] {
  const last = wf.phases.length - 1;
  return wf.phases.map((p, i) => ({
    code: p.code,
    color: p.colors.general ? PHASE_COLORS[p.colors.general] : PHASE_COLORS.RED,
    nextPhases: i < last ? [wf.phases[i + 1].code] : [],
    previousPhases: i > 0 ? [wf.phases[i - 1].code] : [],
    isFinal: i === last,
    isInitial: i === 0,
  }));
}

/** Object types that render as dashed polylines (none currently — STAVAJICI is solid). */
const DASHED_LINE_OBJECT_TYPES = new Set<string>();

for (const wf of GENERATED_WORKFLOWS) {
  if (DEFAULT_WORKFLOW_BY_TYPE.has(wf.objectType)) continue;
  DEFAULT_WORKFLOW_BY_TYPE.set(wf.objectType, {
    objectType: wf.objectType,
    phases: buildLinearPhases(wf),
    lineStyle: DASHED_LINE_OBJECT_TYPES.has(wf.objectType) ? 'dashed' : 'solid',
    lineWeight: 'thick',
  });
}

// =============================================================================
// LOOKUP
// =============================================================================

function getWorkflowsForType(objectType: string): GeneratedWorkflow[] {
  const key = normalizeObjectTypeForLookup(objectType);
  return WORKFLOWS_BY_TYPE.get(key) ?? WORKFLOWS_BY_TYPE.get(objectType) ?? [];
}

function getDefaultGeneratedWorkflow(objectType: string): GeneratedWorkflow | null {
  return getWorkflowsForType(objectType)[0] ?? null;
}

/** Výchozí workflow typu (první větev z Excelu). U BUDOVA = BUDOVA.1. */
export function getWorkflowForType(objectType: string): ObjectTypeWorkflow | null {
  const key = normalizeObjectTypeForLookup(objectType);
  if (!key) return null;
  return DEFAULT_WORKFLOW_BY_TYPE.get(key) ?? DEFAULT_WORKFLOW_BY_TYPE.get(objectType) ?? null;
}

/** Typy objektů s definovaným workflow (bez informativních INFO/STAVAJICI/UNASSIGNED). */
export function getObjectTypesWithWorkflow(): string[] {
  return [...DEFAULT_WORKFLOW_BY_TYPE.keys()].filter((type) => hasWorkflow(type));
}

export function getPhaseInfoByCode(
  phaseCode: string | null | undefined,
): { workflow: GeneratedWorkflow; phase: GeneratedPhase } | null {
  const code = (phaseCode ?? '').trim();
  return code ? PHASE_BY_CODE.get(code) ?? null : null;
}

function toWorkflowPhase(workflow: GeneratedWorkflow, phase: GeneratedPhase): WorkflowPhase {
  const last = workflow.phases.length - 1;
  const transitionBudovaWorkflowId =
    workflow.objectType === 'BUDOVA' ? budovaBranchIdFromPhaseCode(phase.code) : undefined;
  return {
    code: phase.code,
    name: phase.name,
    color: phase.colors.general ? PHASE_COLORS[phase.colors.general] : PHASE_COLORS.RED,
    nextPhases: nextOptionsFromCode(phase.code).map((o) => o.code),
    previousPhases: (() => {
      const prev = previousOptionFromCode(phase.code);
      return prev ? [prev.code] : [];
    })(),
    isFinal: phase.index === last,
    isInitial: phase.index === 0,
    ...(transitionBudovaWorkflowId != null ? { transitionBudovaWorkflowId } : {}),
  };
}

export function getPhaseByCode(objectType: string, phaseCode: string): WorkflowPhase | null {
  void objectType;
  const info = getPhaseInfoByCode(phaseCode);
  if (!info) return null;
  return toWorkflowPhase(info.workflow, info.phase);
}

export function getInitialPhase(objectType: string): WorkflowPhase | null {
  const code = getInitialCodeForType(objectType);
  if (!code) return null;
  const info = getPhaseInfoByCode(code);
  return info ? toWorkflowPhase(info.workflow, info.phase) : null;
}

/** Všechny fáze všech větví daného typu (BUDOVA.1/2/3; ostatní typy = jedna větev). */
export function getAllPhasesForType(objectType: string): WorkflowPhase[] {
  const normalizedType = normalizeObjectTypeForLookup(objectType);
  const seen = new Set<string>();
  const phases: WorkflowPhase[] = [];
  for (const workflow of getWorkflowsForType(normalizedType)) {
    for (let index = 0; index < workflow.phases.length; index += 1) {
      const phase = workflow.phases[index];
      if (seen.has(phase.code)) continue;
      seen.add(phase.code);
      phases.push(toWorkflowPhase(workflow, phase));
    }
  }
  return phases;
}

export function getPhaseColorByCode(
  phaseCode: string | null | undefined,
  drawing: GeneratedDrawingId,
): string | null {
  const info = getPhaseInfoByCode(phaseCode);
  if (!info) return null;
  const key = info.phase.colors[drawing];
  return key ? PHASE_COLORS[key] : null;
}

export function getPhaseColor(objectType: string, phaseCode: string): string {
  void objectType;
  return getPhaseByCode(objectType, phaseCode)?.color ?? PHASE_COLORS.RED;
}

// =============================================================================
// LINEÁRNÍ NAVIGACE
// =============================================================================

export interface PhaseOption {
  name: string;
  code: string;
  index: number;
}

function toOption(phase: GeneratedPhase): PhaseOption {
  return { name: phase.name, code: phase.code, index: phase.index };
}

export function nextOptionsFromCode(phaseCode: string | null | undefined): PhaseOption[] {
  const info = getPhaseInfoByCode(phaseCode);
  if (!info) return [];
  const next = info.workflow.phases[info.phase.index + 1];
  return next ? [toOption(next)] : [];
}

export function previousOptionFromCode(phaseCode: string | null | undefined): PhaseOption | null {
  const info = getPhaseInfoByCode(phaseCode);
  if (!info || info.phase.index <= 0) return null;
  const prev = info.workflow.phases[info.phase.index - 1];
  return prev ? toOption(prev) : null;
}

export function getNextPhaseCodes(_objectType: string, currentPhaseCode: string): string[] {
  void _objectType;
  return nextOptionsFromCode(currentPhaseCode).map((o) => o.code);
}

export function getPreviousPhaseCodes(_objectType: string, currentPhaseCode: string): string[] {
  void _objectType;
  const prev = previousOptionFromCode(currentPhaseCode);
  return prev ? [prev.code] : [];
}

export function getNextPhases(objectType: string, currentPhaseCode: string): WorkflowPhase[] {
  return nextOptionsFromCode(currentPhaseCode)
    .map((o) => getPhaseByCode(objectType, o.code))
    .filter((p): p is WorkflowPhase => p != null);
}

export function getPreviousPhases(objectType: string, currentPhaseCode: string): WorkflowPhase[] {
  const prev = previousOptionFromCode(currentPhaseCode);
  if (!prev) return [];
  const phase = getPhaseByCode(objectType, prev.code);
  return phase ? [phase] : [];
}

export function isValidTransitionByCode(
  fromCode: string | null | undefined,
  toCode: string | null | undefined,
): boolean {
  const to = (toCode ?? '').trim();
  if (!to) return false;
  if (nextOptionsFromCode(fromCode).some((o) => o.code === to)) return true;
  return previousOptionFromCode(fromCode)?.code === to;
}

export function isValidTransition(
  _objectType: string,
  fromPhaseCode: string,
  toPhaseCode: string,
): boolean {
  return isValidTransitionByCode(fromPhaseCode, toPhaseCode);
}

export function isTerminalCode(phaseCode: string | null | undefined): boolean {
  return getPhaseInfoByCode(phaseCode) != null && nextOptionsFromCode(phaseCode).length === 0;
}

export function getInitialCodeForType(objectType: string): string | null {
  return getDefaultGeneratedWorkflow(objectType)?.phases[0]?.code ?? null;
}

const INFORMATIVE_ONLY_OBJECT_TYPES = new Set(['INFO', 'STAVAJICI', 'UNASSIGNED']);

export function hasWorkflow(objectType: string): boolean {
  const t = (objectType ?? '').trim().toUpperCase();
  if (!t || INFORMATIVE_ONLY_OBJECT_TYPES.has(t)) return false;
  return getWorkflowForType(t) != null;
}

/** Styl čáry na mapě. VYKOP je vždy plná čára. */
export function getLineStyle(objectType: string): 'solid' | 'dashed' {
  const ot = (objectType ?? '').trim().toUpperCase();
  if (ot === 'VYKOP') return 'solid';
  return getWorkflowForType(objectType)?.lineStyle ?? 'solid';
}

/** Informativní trasy: vždy kanonická jediná fáze (null/legacy status → správná barva na mapě). */
const INFORMATIVE_ROUTE_MAP_DISPLAY_PHASE: Record<string, string> = {
  STAVAJICI: 'STAVAJICI.1.PHASE.0',
};

export function resolveMapDisplayPhaseCode(
  objectType: string | null | undefined,
  status: string | null | undefined,
): string | null {
  const normalizedType = normalizeObjectTypeForLookup(String(objectType ?? ''));
  const phase = (status ?? '').trim();
  const forced = INFORMATIVE_ROUTE_MAP_DISPLAY_PHASE[normalizedType];

  if (!forced) {
    return phase || null;
  }

  if (!phase || phase !== forced) {
    return forced;
  }

  return phase;
}

// =============================================================================
// IDENTITA FÁZE PODLE NÁZVU (BUDOVA varianty)
// =============================================================================

export function codesWithSamePhaseName(code: string | null | undefined): string[] {
  const info = getPhaseInfoByCode(code);
  if (!info) return [];
  const { objectType } = info.workflow;
  const name = info.phase.name;
  return GENERATED_WORKFLOWS.filter((w) => w.objectType === objectType).flatMap((w) =>
    w.phases.filter((p) => p.name === name).map((p) => p.code),
  );
}

export function isSamePhase(status: string | null | undefined, referenceCode: string): boolean {
  const s = (status ?? '').trim();
  if (!s) return false;
  if (s === referenceCode) return true;
  return codesWithSamePhaseName(referenceCode).includes(s);
}

export function hasReachedWorkflowMilestone(
  status: string | null | undefined,
  milestoneReferenceCode: string,
): boolean {
  const currentInfo = getPhaseInfoByCode(status);
  if (!currentInfo) return false;
  const milestonePhase = currentInfo.workflow.phases.find((p) =>
    isSamePhase(p.code, milestoneReferenceCode),
  );
  if (!milestonePhase) return false;
  return currentInfo.phase.index >= milestonePhase.index;
}

// =============================================================================
// PROGRESS
// =============================================================================

export function progressPercentByCode(phaseCode: string | null | undefined): number {
  const info = getPhaseInfoByCode(phaseCode);
  if (!info) return 0;
  const len = info.workflow.phases.length;
  if (len <= 1) return 100;
  const pct = (info.phase.index / (len - 1)) * 100;
  return Math.round(Math.max(0, Math.min(100, pct)) * 10) / 10;
}
